import {
  AddCircle,
  AddCircleRounded,
  DashboardCustomize,
  DeleteRounded,
  FilePresentRounded,
} from "@mui/icons-material";
import VideoIcon from "@mui/icons-material/VideoLibrary";
import {
  Card,
  CardContent,
  Dialog,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Category, Status_Type, SubCategory } from "src/types";
import AxiosInstance from "../../API/axios";
import { BASE_API_URL } from "../../API/Base";

interface Lesson {
  // lesson_id: number;
  title: string;
  duration: string;
  video_type: string;
  video_url: string;
  lesson_type: string;
  attachment_url: string;
  attachment_type: string;
}

interface Section {
  // section_id: number;
  title: string;
  lessons: Lesson[];
}

interface FormData {
  title: string;
  short_description: string;
  outcome: string;
  requirements: string;
  price: number;
  status: boolean;
  duration: string;
  exam_type: string;
  category_id: string;
  subcategory_id: string;
  sections: Section[];
}

const initialLessonState: Lesson = {
  // lesson_id: 0,
  title: "",
  duration: "",
  video_type: "",
  video_url: "",
  lesson_type: "",
  attachment_url: "",
  attachment_type: "",
};

const initialSectionState: Section = {
  // section_id: 0,
  title: "",
  lessons: [initialLessonState],
};

const initialFormState: FormData = {
  title: "",
  short_description: "",
  outcome: "",
  requirements: "",
  price: 0,
  status: true,
  duration: "",
  exam_type: "",
  category_id: "",
  subcategory_id: "",
  sections: [initialSectionState],
};

const AddCourse: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  // const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [thumbnailName, setThumbnailName] = useState<string>("");
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubCategories] = useState<SubCategory[]>([]);
  const [examType, setExamType] = useState<Status_Type[]>([]);
  const [lesType, setLesType] = useState<Status_Type[]>([]);
  const [section, setSection] = useState<Section[]>([]);
  const [lessonTitle, setLessonTitle] = useState<string>("");
  const [lessonDuration, setLessonDuration] = useState<string>("");
  const [lessonVideoType, setLessonVideoType] = useState<string>("");
  const [lessonVideoUrl, setLessonVideoUrl] = useState<string>("");
  const [lessonLessonType, setLessonLessonType] = useState<string>("");
  const [lessonAttachmentUrl, setLessonAttachmentUrl] = useState<string>("");
  const [lessonAttachmentType, setLessonAttachmentType] = useState<string>("");
  const [lessonModalOpen, setLessonModalOpen] = useState<{
    open: boolean;
    section: Section | null;
  }>({
    open: false,
    section: null,
  });

  const navigate = useNavigate();

  // const simulateUpload = () => {
  //   // Simulate upload progress with setTimeout
  //   const interval = setInterval(() => {
  //     // Increase progress by 10% every 500ms
  //     setUploadProgress((prevProgress) => prevProgress + 10);

  //     // Stop interval when progress reaches 100%
  //     if (uploadProgress >= 100) {
  //       clearInterval(interval);
  //     }
  //   }, 500);
  // };

  const fetchCategory = async () => {
    try {
      const response = await AxiosInstance.get(
        "/admin/courses/v1/get-category"
      );
      setCategories(response.data); // Directly set the categories state
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSubcategory = async () => {
    try {
      const response = await AxiosInstance.get(
        "/admin/courses/v1/get-sub-category"
      );
      setSubCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  const fetchExamType = async () => {
    try {
      const response = await AxiosInstance.get<Status_Type[]>(
        "/admin/courses/v1/get-exam-type"
      );
      setExamType(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchLessonType = async () => {
    try {
      const response = await AxiosInstance.get("/admin/courses/v1/lesson-type");
      setLesType(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  useEffect(() => {
    fetchCategory();
    fetchExamType();
    fetchSubcategory();
    fetchLessonType();
  }, []);

  const handleChange = (key: string, value: any) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setThumbnailName(selectedFile.name);
      setFile(selectedFile);

      const previewURL = URL.createObjectURL(selectedFile);
      setImagePreview(previewURL);
    }
  };

  const handleAddSection = () => {
    const title = formData.sections[formData.sections.length - 1].title.trim();
    if (title !== "") {
      const newSection = {
        title: title,
        lessons: [],
      };
      setSection((prevSection) => [...prevSection, newSection]);

      // Reset the title field in the form state
      setFormData((prevFormData) => ({
        ...prevFormData,
        sections: [
          ...prevFormData.sections.slice(0, -1),
          {
            title: "",
            lessons: [],
          },
        ],
      }));
    }
  };

  const handleAddLesson = (section: Section) => {
    const newLesson = {
      title: lessonTitle.trim(),
      duration: lessonDuration.trim(),
      video_type: lessonVideoType.trim(),
      video_url: lessonVideoUrl.trim(),
      lesson_type: lessonLessonType.trim(),
      attachment_url: lessonAttachmentUrl.trim(),
      attachment_type: lessonAttachmentType.trim(),
    };

    setSection((prevSections) => {
      const updatedSections = [...prevSections]; // Create a shallow copy of the sections array

      const sectionIndex = updatedSections.findIndex(
        (s) => s.title === section.title
      );
      if (sectionIndex !== -1) {
        updatedSections[sectionIndex] = {
          ...section,
          lessons: [...section.lessons, newLesson],
        };
      }

      return updatedSections;
    });

    setLessonModalOpen({ open: false, section: null }); // Close the lesson modal
  };

  const handleAddLessonModal = (section: Section) => {
    setLessonModalOpen({ open: true, section });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newData = {
        title: formData.title,
        short_description: formData.short_description,
        outcome: formData.outcome,
        requirements: formData.requirements,
        price: formData.price,
        thumbnail: "null",
        status: 1,
        duration: formData.duration,
        exam_type: formData.exam_type,

        category_id: String(formData.category_id),

        subcategory_id: String(formData.subcategory_id) || "0",
        sections: section,
      };

      const apiurl = `${BASE_API_URL}/admin/courses/v1/create-course-with-sections-and-lessons`;
      const res = await AxiosInstance.post(apiurl, newData);
      if (res.status === 200) {
        const courseId = res.data.course_id;

        await handleUploadThumbnail(courseId);
      } else {
        console.error("Error adding course:");
      }
    } catch (error) {
      console.error("Error adding course:", error);
    }
  };

  const handleUploadThumbnail = async (courseId: string) => {
    try {
      if (file) {
        // Check if file is not null
        const formData = new FormData();
        formData.append("file", file);

        const response = await AxiosInstance.post(
          `${BASE_API_URL}/admin/courses/v1/upload-media/${courseId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          // Thumbnail uploaded successfully
          toast.success("Course created successfully");
        } else {
          // Handle other status codes or errors
          console.error("Error uploading thumbnail:", response.data.message);
        }
      } else {
        console.error("File is null. Cannot upload thumbnail.");
      }
    } catch (error) {
      // Handle any network or other errors
      console.error("Error uploading thumbnail:", error);
    }
  };

  return (
    <div className="flex flex-col items-start justify-start w-full h-full max-w-6xl mx-auto mt-4">
      <div className="flex flex-col w-full mb-4">
        <div className="flex flex-row items-center p-2 mt-6 rounded-md bg-primary_shade">
          <DashboardCustomize className="mr-2 text-3xl text-blue" />
          <p className="text-xl font-semibold leading-3 tracking-normal text-blue">
            Add course banner
          </p>
        </div>
        <div className="container flex flex-col items-center justify-center w-full px-4 py-4 mt-8 border border-dashed rounded-md border-blue/25">
          {file ? (
            <div className="w-full">
              <div className="flex items-center justify-between w-full p-4 mb-6 rounded-md shadow-sm bg-primary_shade">
                <div className="flex items-center flex-start">
                  <FilePresentRounded className="mr-2 text-blue" />
                  <h3 className="text-base font-semibold text-blue">
                    {thumbnailName}
                  </h3>
                </div>
                <div>
                  <DeleteRounded
                    className="ml-4 cursor-pointer text-blue"
                    onClick={() => setFile(null)}
                  />
                </div>
              </div>
              {imagePreview && (
                // <Image
                //   src={imagePreview}
                //   alt="Uploaded thumbnail"
                //   className="w-full mb-2 rounded-md h-60 aspect-auto"
                // />
                <img
                  src={imagePreview}
                  alt="Uploaded thumbnail"
                  className="object-contain w-full mb-2 rounded-md h-72 aspect-auto"
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center"
              >
                <AddCircleRounded
                  sx={{ fontSize: "2rem" }}
                  className="mr-2 cursor-pointer text-blue"
                />
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <h3 className="text-base text-zinc-500">
                  Choose file to upload
                </h3>
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-row items-center w-full p-2 mt-6 mb-4 rounded-md bg-primary_shade">
        <DashboardCustomize className="mr-2 text-3xl text-blue" />
        <p className="text-xl font-semibold leading-3 tracking-normal text-blue">
          Add course information
        </p>
      </div>
      <form>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Title"
              name="title"
              fullWidth
              margin="dense"
              size="small"
              onChange={(e) => handleChange("title", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Short Description"
              fullWidth
              margin="dense"
              size="small"
              name="short_description"
              value={formData.short_description}
              onChange={(e) =>
                handleChange("short_description", e.target.value)
              }
              className="w-full p-2 mt-1 border border-gray-300 rounded-md"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Outcome"
              fullWidth
              margin="dense"
              size="small"
              name="outcome"
              value={formData.outcome}
              onChange={(e) => handleChange("outcome", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Requirements"
              fullWidth
              margin="dense"
              size="small"
              name="requirements"
              value={formData.requirements}
              onChange={(e) => handleChange("requirements", e.target.value)}
              className="w-full p-2 mt-1 border border-gray-300 rounded-md"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl
              fullWidth
              variant="outlined"
              margin="dense"
              size="small"
            >
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category_id}
                label="Category"
                name="category_id"
                onChange={(e) => handleChange("category_id", e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem value={category.Category_Id}>
                    {category.Category_Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl
              fullWidth
              variant="outlined"
              margin="dense"
              size="small"
            >
              <InputLabel>Sub Category</InputLabel>
              <Select
                value={formData.subcategory_id}
                label="SubCategory"
                name="subcategory_id"
                onChange={(e) => handleChange("subcategory_id", e.target.value)}
              >
                {subcategories.map((sub) => (
                  <MenuItem value={sub.Sub_Category_Id}>
                    {sub.Sub_Category_Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Price"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              name="price"
              value={formData.price}
              onChange={(e) => handleChange("price", e.target.value)}
              className="w-full p-2 mt-1 border border-gray-300 rounded-md"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Duration"
              fullWidth
              margin="dense"
              size="small"
              type="text"
              name="duration"
              value={formData.duration}
              onChange={(e) => handleChange("duration", e.target.value)}
              className="w-full p-2 mt-1 border border-gray-300 rounded-md"
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl
              fullWidth
              variant="outlined"
              margin="dense"
              size="small"
            >
              <InputLabel>Exam type</InputLabel>
              <Select
                value={formData.exam_type}
                label="Exam type"
                name="exam_type"
                onChange={(e) => handleChange("exam_type", e.target.value)}
              >
                {examType.map((type) => (
                  <MenuItem value={type.Status_Name}>
                    {type.Status_Name}
                  </MenuItem>
                ))}
                {/* <MenuItem value={"true"}>Active</MenuItem>
                <MenuItem value={"false"}>Inactive</MenuItem> */}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <div className="flex flex-row items-center justify-between w-full p-2 mt-6 mb-4 rounded-md bg-primary_shade">
          <div className="flex items-center mr-4">
            <DashboardCustomize className="mr-2 text-3xl text-blue " />
            <p className="text-xl font-semibold leading-3 tracking-normal text-blue">
              Add sections and lessons
            </p>
          </div>
        </div>

        {formData.sections.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            className="flex flex-row items-center justify-center mb-8 text-center"
          >
            {/* <Grid item xs={6}>
              <TextField
                type="text"
                fullWidth
                margin="dense"
                size="small"
                name="title"
                label="Section Title"
                value={section.title}
                onChange={(e) => {
                  const updatedSections = [...formData.sections];
                  updatedSections[sectionIndex].title = e.target.value;
                  setFormData({
                    ...formData,
                    sections: updatedSections,
                  });
                }}
                className="w-2/3 border border-gray-300 rounded-md"
              />
            </Grid> */}
            <div className="w-full max-w-md mx-auto mt-4">
              <div className="relative ">
                <input
                  type="text"
                  className="block w-full p-4 text-gray-900 rounded-lg ps-10 bg-zinc-100 focus:outline-none focus:ring-primary"
                  placeholder="Add new section"
                  required
                  value={section.title}
                  onChange={(e) => {
                    const updatedSections = [...formData.sections];
                    updatedSections[sectionIndex].title = e.target.value;
                    setFormData({
                      ...formData,
                      sections: updatedSections,
                    });
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddSection}
                  className="text-white absolute end-2.5 bottom-2.5 bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 "
                >
                  Add new section
                </button>
              </div>
            </div>
          </div>
        ))}

        {section.length > 0 && (
          <div className="container w-full mx-auto">
            <Grid container spacing={10}>
              {section.map((section, sectionIndex) => (
                <Grid item xs={6} key={sectionIndex}>
                  <Card>
                    <CardContent
                      sx={{
                        padding: "8px 0px",
                        borderTop: "3px solid #00A99D",
                        borderRadius: "5px",
                      }}
                    >
                      <div className="px-4 py-2">
                        <div className="flex flex-row items-center justify-between w-full mb-4">
                          <h2 className="text-lg font-semibold text-black md:text-xl ">
                            Section {sectionIndex + 1} : {section.title}
                          </h2>
                          <div>
                            <div
                              className="flex items-center"
                              onClick={() => handleAddLessonModal(section)}
                            >
                              <AddCircle className="mr-2 text-3xl cursor-pointer text-primary " />
                            </div>
                          </div>
                        </div>

                        {section.lessons.length > 0 && (
                          <ul>
                            {section.lessons.map((lesson, lessonIndex) => (
                              <li key={lessonIndex}>
                                <div className="flex flex-row items-center justify-between w-full mb-4">
                                  <div className="flex items-center justify-between">
                                    <VideoIcon
                                      style={{
                                        marginRight: "8px",
                                        fontSize: 20,
                                        color: "#00A99D",
                                      }}
                                    />
                                    <h2 className="text-base">
                                      {lesson.title}
                                    </h2>
                                  </div>
                                  <h2 className="text-base">
                                    {lesson.duration}
                                  </h2>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        )}

        <Dialog
          open={lessonModalOpen.open}
          onClose={() => setLessonModalOpen({ open: false, section: null })}
        >
          <div className="fixed w-full max-w-screen-md p-4 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg top-1/2 left-1/2 md:w-1/2 md:p-8">
            <h2 className="px-4 py-2 text-lg font-semibold md:text-xl bg-primary_shade text-blue">
              Add new lesson
            </h2>
            <div className="w-full mt-4">
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Lesson Title"
                    value={lessonTitle}
                    onChange={(e) => setLessonTitle(e.target.value)}
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Duration"
                    value={lessonDuration}
                    onChange={(e) => setLessonDuration(e.target.value)}
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Video Type"
                    value={lessonVideoType}
                    onChange={(e) => setLessonVideoType(e.target.value)}
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Video URL"
                    value={lessonVideoUrl}
                    onChange={(e) => setLessonVideoUrl(e.target.value)}
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    size="small"
                  >
                    <InputLabel>Lesson type</InputLabel>
                    <Select
                      value={lessonLessonType}
                      label="Lesson type"
                      onChange={(e) =>
                        setLessonLessonType(e.target.value as string)
                      }
                    >
                      {lesType.map((type) => (
                        <MenuItem value={type.Status_Name}>
                          {type.Status_Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Attachment URL"
                    value={lessonAttachmentUrl}
                    onChange={(e) => setLessonAttachmentUrl(e.target.value)}
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Attachment Type"
                    value={lessonAttachmentType}
                    onChange={(e) => setLessonAttachmentType(e.target.value)}
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                </Grid>
              </Grid>
            </div>
            <div className="flex items-center justify-between w-full mt-10">
              <button
                type="button"
                className="px-4 py-2 border rounded-md border-primary text-primary"
                onClick={() =>
                  setLessonModalOpen({ open: false, section: null })
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-white rounded-md bg-primary"
                onClick={() =>
                  lessonModalOpen.section &&
                  handleAddLesson(lessonModalOpen.section)
                }
              >
                Save changes
              </button>
            </div>
          </div>
        </Dialog>

        <div className="flex items-center justify-between w-full mt-20">
          <button
            type="button"
            className="px-4 py-2 border rounded-md border-primary text-primary"
            onClick={() => navigate("/courses/manage")}
          >
            Back
          </button>
          <button
            type="button"
            className="px-4 py-2 text-white rounded-md bg-primary"
            onClick={handleSubmit}
          >
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCourse;
